import {
  Button,
  Dialog,
  LoadingIndicator,
  Plus,
  useFoundationColorTokens,
  useRadioButtonColorTokens,
  useSpacing,
} from "@surya-digital/leo-reactjs-material-ui";
import { observer } from "mobx-react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useAssignEmailCycleStore,
  useEmailCycleNamesStore,
} from "../../stores/Hooks";
import { useRouteNavigator } from "../../../../routes/RoutesHelper";
import {
  FormControlLabel,
  FormControlLabelProps,
  Radio,
  RadioGroup,
  Stack,
} from "@mui/material";
import { UnexpectedErrorDialog } from "../../../../components/dialogs/UnexpectedErrorDialog";
import { AssignEmailCycleRPC } from "@pulse/pulse-rpcs/lib/email-cycle/assignEmailCycleRPC";
import { ErrorDialog } from "../../../../components/dialogs/ErrorDialog";
import { getRadioButtonStyleProps } from "@pulse/shared-components";

interface ChooseEmailCycleDialogProps {
  setOpenEmailQueuedSuccessfulDialog: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  handleOpenCreateEmailCycleDrawer: VoidFunction;
  getEmailCycleNames: VoidFunction;
  closeDialog: VoidFunction;
}

export const ChooseEmailCycleDialog: React.FC<ChooseEmailCycleDialogProps> =
  observer(
    ({
      setOpenEmailQueuedSuccessfulDialog,
      handleOpenCreateEmailCycleDrawer,
      getEmailCycleNames,
      closeDialog,
    }) => {
      const [
        openAssignEmailCycleUnexpectedErrorDialog,
        setOpenAssignEmailCycleUnexpectedErrorDialog,
      ] = useState(false);

      const { t } = useTranslation();
      const radioButtonTokens = useRadioButtonColorTokens();
      const tokens = useFoundationColorTokens();
      const spacing = useSpacing();
      const navigateTo = useRouteNavigator();

      const emailCycleNamesStore = useEmailCycleNamesStore();
      const assignEmailCycleStore = useAssignEmailCycleStore();

      const radioProps = getRadioButtonStyleProps(
        tokens,
        radioButtonTokens,
        spacing,
        spacing.spaceXS,
      );
      const radioButtonsFormControlLabelProps: FormControlLabelProps[] =
        emailCycleNamesStore.emailCycleNames.map(({ scheduleId, name }) => ({
          control: <Radio {...radioProps} />,
          label: name.name,
          value: scheduleId,
        }));

      const onPrimaryButtonClick = async (): Promise<void> => {
        assignEmailCycleStore
          .assignEmailCycle()
          .then(() => {
            if (!assignEmailCycleStore.assignEmailCycleRPCError) {
              setOpenEmailQueuedSuccessfulDialog(true);
            }
          })
          .catch(() => setOpenAssignEmailCycleUnexpectedErrorDialog(true));
      };

      const handleRefetchEmailCycleNames = (): void => {
        assignEmailCycleStore.resetRpcError();
        getEmailCycleNames();
      };

      if (assignEmailCycleStore.loadingAssignEmailCycle) {
        return <LoadingIndicator variant="overlay" isLoading />;
      }

      if (openAssignEmailCycleUnexpectedErrorDialog) {
        return (
          <UnexpectedErrorDialog
            message={t("emailCycles.assignEmailCycleUnexpectedErrorMessage")}
            onClickDone={closeDialog}
          />
        );
      }

      // Handling RPC Errors
      if (assignEmailCycleStore.assignEmailCycleRPCError) {
        switch (assignEmailCycleStore.assignEmailCycleRPCError) {
          case AssignEmailCycleRPC.RPCError.InvalidProjectId:
          case AssignEmailCycleRPC.RPCError.InvalidScheduleId:
            navigateTo.pageNotFound();
            break;
          case AssignEmailCycleRPC.RPCError.EmailCycleInProgress:
            return (
              <ErrorDialog
                title={t("emailCycles.emailCycleInProgressErrorTitle")}
                message={t("emailCycles.emailCycleInProgressErrorMessage")}
                onClickClose={handleRefetchEmailCycleNames}
              />
            );
          case AssignEmailCycleRPC.RPCError.EmailCycleInactive:
            return (
              <ErrorDialog
                title={t("emailCycles.emailCycleExpiredErrorTitle")}
                message={t("emailCycles.emailCycleExpiredErrorMessage")}
                onClickClose={handleRefetchEmailCycleNames}
              />
            );
          default:
            assignEmailCycleStore.assignEmailCycleRPCError satisfies never;
        }
        return null;
      }

      // Choose Email Cycle Dialog
      return (
        <Dialog
          open={true}
          title={t("emailCycles.chooseEmailCycle")}
          primaryButtonText={t("emailCycles.assignEmailCycle")}
          isPrimaryButtonDisabled={!assignEmailCycleStore.scheduleId}
          onPrimaryButtonClick={onPrimaryButtonClick}
          secondaryButtonText={t("common.close")}
          onSecondaryButtonClick={closeDialog}
          onClose={assignEmailCycleStore.resetStore}
          disableBackdropClick={true}
        >
          <Stack gap={spacing.spaceLG}>
            <Button
              label={t("emailCycles.createNewCycle")}
              name="createNewCycleButton"
              variant="outlined-color"
              size="medium"
              icon={<Plus />}
              onClick={handleOpenCreateEmailCycleDrawer}
            />
            <RadioGroup
              value={assignEmailCycleStore.scheduleId}
              onChange={(_, value) => assignEmailCycleStore.setCycleId(value)}
            >
              {radioButtonsFormControlLabelProps.map(
                (formControlLabelProps, index) => (
                  <FormControlLabel key={index} {...formControlLabelProps} />
                ),
              )}
            </RadioGroup>
          </Stack>
        </Dialog>
      );
    },
  );
